/**
 * A press that opens one section of the Overview — the report's brief, read in
 * place — rather than the tab at its lede.
 *
 * The Overview had no way in below its head except its own connections section,
 * so a line that counted what a run found could only send the reader to a tab of
 * shut folds and leave the finding to them. This is the deep link: the tab, the
 * fold open, the section brought into view, and — when the press took focus with
 * it, as a press in the thread does when the tab replaces it — focus on the
 * section's own heading (D217's rule: focus lands on what replaced the press).
 *
 * `runFocusId`'s contract, kept in this module rather than the store because
 * nothing but the Overview reads it: the ask MOUNTS the surface it arms, the
 * Overview takes it on the way in and clears it, and reopening the tab later lands
 * on the lede as it always has. The ask carries the exploration it was made on,
 * and one made on another graph is dropped rather than opening a fold nobody asked
 * for there. Never persisted.
 */

import { useEffect } from 'react'
import { useStore } from '../core/store'
import type { FoundSection } from '../core/runFound'

let ask: { session: string; id: FoundSection } | null = null
const takers = new Set<() => void>()

/** Open the Overview on one section. Free: it switches a tab and a fold. */
export function openReportSection(id: FoundSection): void {
  const s = useStore.getState()
  ask = { session: s.session.id, id }
  s.setRail(true)
  s.setRightTab('breakthroughs')
  // An Overview already on screen takes it now; one that mounts takes it then.
  for (const take of takers) take()
}

/** The Overview's half: `onAsk` runs once per ask made on this exploration. */
export function useReportSectionAsk(sessionId: string, onAsk: (id: FoundSection) => void): void {
  useEffect(() => {
    const take = () => {
      const a = ask
      if (!a) return
      ask = null
      if (a.session === sessionId) onAsk(a.id)
    }
    take()
    takers.add(take)
    return () => {
      takers.delete(take)
    }
  }, [sessionId, onAsk])
}
